import React, { useState, useEffect, useRef, useCallback } from "react";
import "./singleCourse.css";

// ── COURSE DATA ───────────────────────────────────────────────────────────────
const COURSE_DATA = {
  title: "Complete Web Development Bootcamp 2025",
  subtitle:
    "Master HTML, CSS, JavaScript, React, Node.js & MongoDB — from zero to deploying production-ready apps.",
  category: "Web Development",
  level: "Intermediate",
  language: "English",
  rating: 4.8,
  ratingCount: 3241,
  enrolledCount: 12480,
  totalHours: "40h 30m",
  totalLessons: 142,
  tags: ["react", "nodejs", "javascript", "mongodb", "css"],
  price: 99,
  discountPrice: 49,
  status: "published",
  thumbnail: null,
  instructor: {
    name: "Dr. Ali Ahmad",
    initials: "DA",
    title: "Senior Software Engineer & Educator",
    rating: 4.9,
    students: 48200,
    courses: 12,
    bio: "Dr. Ali Ahmad is a seasoned software engineer with over 12 years of industry experience at leading tech companies. He holds a PhD in Computer Science and has a passion for making complex concepts accessible to everyone. His practical, project-based teaching approach has helped tens of thousands of students land jobs at top companies worldwide.",
  },
  outcomes: [
    "Build responsive, production-ready web applications",
    "Master React hooks, context, and state management",
    "Create RESTful APIs with Node.js & Express",
    "Work with MongoDB and database design",
    "Deploy applications to cloud platforms",
    "Implement authentication & security best practices",
  ],
  prerequisites: [
    "Basic knowledge of HTML and CSS",
    "Familiarity with any programming language",
    "A laptop/desktop computer with internet access",
    "Willingness to practice and build projects",
  ],
  targetAudience: [
    "Aspiring front-end developers with basic coding experience",
    "Back-end developers who want to learn front-end skills",
    "Entrepreneurs who want to build their own web apps",
    "Computer science students wanting practical experience",
  ],
  description: `Welcome to the most comprehensive web development bootcamp on the platform. Whether you're an absolute beginner or an experienced developer looking to sharpen your full-stack skills, this course takes you step by step through everything you need to become a professional web developer.\n\nYou'll start from the very basics of HTML and CSS, progressively building towards advanced JavaScript, modern React.js, server-side Node.js, and database management with MongoDB. Every concept is taught with real-world projects so you can see exactly how professional developers build software.\n\nBy the end of this course, you'll have built multiple full-stack applications that you can showcase in your portfolio, and you'll have the confidence to tackle any web development challenge.`,
  modules: [
    {
      id: "m1",
      title: "Module 1: Introduction to Web Development",
      lessonCount: 8,
      duration: "2h 10m",
      lessons: [
        {
          id: "l1",
          title: "Welcome & Course Overview",
          type: "video",
          duration: "05:24",
          isFreePreview: true,
        },
        {
          id: "l2",
          title: "How the Web Works",
          type: "video",
          duration: "12:30",
          isFreePreview: false,
        },
        {
          id: "l3",
          title: "Setting Up Your Development Environment",
          type: "video",
          duration: "18:45",
          isFreePreview: true,
        },
        {
          id: "l4",
          title: "Section Quiz: Web Basics",
          type: "quiz",
          duration: null,
          isFreePreview: false,
        },
      ],
    },
    {
      id: "m2",
      title: "Module 2: HTML Fundamentals",
      lessonCount: 14,
      duration: "4h 20m",
      lessons: [
        {
          id: "l5",
          title: "HTML Document Structure",
          type: "video",
          duration: "14:00",
          isFreePreview: false,
        },
        {
          id: "l6",
          title: "Semantic HTML & Accessibility",
          type: "video",
          duration: "21:15",
          isFreePreview: false,
        },
        {
          id: "l7",
          title: "HTML5 Cheatsheet",
          type: "article",
          duration: null,
          isFreePreview: false,
        },
      ],
    },
    {
      id: "m3",
      title: "Module 3: CSS & Modern Layouts",
      lessonCount: 16,
      duration: "5h 45m",
      lessons: [
        {
          id: "l8",
          title: "CSS Selectors & Specificity",
          type: "video",
          duration: "18:20",
          isFreePreview: false,
        },
        {
          id: "l9",
          title: "Flexbox Deep Dive",
          type: "video",
          duration: "26:40",
          isFreePreview: false,
        },
        {
          id: "l10",
          title: "CSS Grid Mastery",
          type: "video",
          duration: "31:05",
          isFreePreview: false,
        },
      ],
    },
    {
      id: "m4",
      title: "Module 4: JavaScript Essentials",
      lessonCount: 22,
      duration: "8h 00m",
      lessons: [
        {
          id: "l11",
          title: "Variables, Types & Scope",
          type: "video",
          duration: "22:00",
          isFreePreview: false,
        },
        {
          id: "l12",
          title: "Async JS: Promises & Async/Await",
          type: "video",
          duration: "35:10",
          isFreePreview: false,
        },
      ],
    },
    {
      id: "m5",
      title: "Module 5: React.js & Modern Frontend",
      lessonCount: 20,
      duration: "7h 15m",
      lessons: [
        {
          id: "l13",
          title: "Components & JSX",
          type: "video",
          duration: "19:30",
          isFreePreview: false,
        },
        {
          id: "l14",
          title: "Hooks: useState, useEffect, useContext",
          type: "video",
          duration: "42:00",
          isFreePreview: false,
        },
      ],
    },
  ],
  reviews: [
    {
      id: "r1",
      name: "Sara Z.",
      initials: "SZ",
      stars: 5,
      date: "Jan 2025",
      text: "This course completely transformed my career. The way Dr. Ahmad explains complex concepts is unlike anything I've seen. Landed a job 2 months after completing it!",
    },
    {
      id: "r2",
      name: "Muhammad K.",
      initials: "MK",
      stars: 5,
      date: "Dec 2024",
      text: "Best investment I've made. Every module is packed with practical knowledge. The React section alone is worth the full price. Highly recommended!",
    },
    {
      id: "r3",
      name: "Ayesha B.",
      initials: "AB",
      stars: 4,
      date: "Nov 2024",
      text: "Very comprehensive course. The projects are really well thought out. Would love more advanced content on TypeScript integration. Overall great experience.",
    },
  ],
  ratingBreakdown: [
    { stars: 5, pct: 78 },
    { stars: 4, pct: 15 },
    { stars: 3, pct: 5 },
    { stars: 2, pct: 2 },
  ],
  relatedCourses: [
    {
      id: "rc1",
      emoji: "🎨",
      title: "UI/UX Design Masterclass",
      instructor: "Prof. Ayesha Tariq",
      rating: 4.7,
      price: 39,
    },
    {
      id: "rc2",
      emoji: "🐍",
      title: "Python for Data Science",
      instructor: "Eng. Bilal Hassan",
      rating: 4.9,
      price: 59,
    },
    {
      id: "rc3",
      emoji: "☁️",
      title: "AWS Cloud Practitioner",
      instructor: "Ms. Zara Malik",
      rating: 4.8,
      price: 69,
    },
    {
      id: "rc4",
      emoji: "🔐",
      title: "Cybersecurity Fundamentals",
      instructor: "Dr. Ali Ahmad",
      rating: 4.6,
      price: 49,
    },
  ],
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
const StarRating = ({ value, max = 5 }) => (
  <div className="sc-stars">
    {Array.from({ length: max }, (_, i) => {
      const filled = i + 1 <= Math.floor(value);
      const half = !filled && i < value;
      return (
        <span
          key={i}
          className={`sc-star ${filled ? "filled" : half ? "half" : ""}`}
        >
          ★
        </span>
      );
    })}
  </div>
);

const LessonTypeIcon = ({ type }) => {
  if (type === "video")
    return (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    );
  if (type === "quiz")
    return (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <circle cx="3" cy="6" r="1" fill="currentColor" />
        <circle cx="3" cy="12" r="1" fill="currentColor" />
        <circle cx="3" cy="18" r="1" fill="currentColor" />
      </svg>
    );
  if (type === "article")
    return (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    );
  return null;
};

function useCountdown(initialSeconds) {
  const [secs, setSecs] = useState(initialSeconds);
  useEffect(() => {
    if (secs <= 0) return;
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

function RatingBar({ pct, stars }) {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setWidth(pct);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [pct]);
  return (
    <div className="sc-rating-bar-row" ref={ref}>
      <div className="sc-rating-bar-track">
        <div className="sc-rating-bar-fill" style={{ width: `${width}%` }} />
      </div>
      <span className="sc-rating-bar-stars">{"★".repeat(stars)}</span>
      <span className="sc-rating-bar-pct">{pct}%</span>
    </div>
  );
}

// ── HOME ICON ─────────────────────────────────────────────────────────────────
const HomeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
  </svg>
);

// ── ENROLL CARD ───────────────────────────────────────────────────────────────
function EnrollCard({ course }) {
  const [enrolled, setEnrolled] = useState(false);
  const [wished, setWished] = useState(false);
  const [copied, setCopied] = useState(false);
  const countdown = useCountdown(2 * 3600 + 14 * 60 + 33);
  const discount = Math.round((1 - course.discountPrice / course.price) * 100);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="sc-enroll-card">
      <div className="sc-enroll-thumb">
        {course.thumbnail ? (
          <img src={course.thumbnail} alt="Course preview" />
        ) : (
          <div className="sc-thumb-placeholder">
            <svg
              width="44"
              height="44"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            <span>Preview Course</span>
          </div>
        )}
      </div>
      <div className="sc-enroll-body">
        <div className="sc-price-block">
          <span className="sc-price-current">${course.discountPrice}</span>
          <span className="sc-price-original">${course.price}</span>
          <span className="sc-price-badge">{discount}% off</span>
        </div>
        <div className="sc-price-timer">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <span className="sc-countdown">{countdown}</span> left at this price!
        </div>
        <button
          className={`sc-enroll-btn ${enrolled ? "enrolled" : ""}`}
          onClick={() => setEnrolled(true)}
        >
          {enrolled ? "✓ Enrolled!" : "Enroll Now"}
        </button>
        <button
          className={`sc-wishlist-btn ${wished ? "wished" : ""}`}
          onClick={() => setWished((w) => !w)}
        >
          {wished ? "♥ Saved to Wishlist" : "♡ Add to Wishlist"}
        </button>
        <p className="sc-money-back">30-Day Money-Back Guarantee</p>
        <ul className="sc-includes">
          {[
            [
              <svg
                key="v"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" />
              </svg>,
              `${course.totalHours} on-demand video`,
            ],
            [
              <svg
                key="f"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              </svg>,
              "Downloadable resources",
            ],
            [
              <svg
                key="l"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>,
              "Lifetime access",
            ],
          ].map(([icon, text], i) => (
            <li key={i}>
              {icon}
              {text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function SingleCoursePage({
  course = COURSE_DATA,
  onNavigateHome,
  onNavigateCourses,
  onNavigateCategory,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [descExpanded, setDescExpanded] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedModules, setExpandedModules] = useState({ 0: true });
  const [pastHero, setPastHero] = useState(false);
  const heroRef = useRef(null);

  // Refs for each section to scroll to
  const sectionRefs = {
    overview: useRef(null),
    curriculum: useRef(null),
    instructor: useRef(null),
    reviews: useRef(null),
  };

  // Track which section is in view for active tab highlight
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => setPastHero(!e.isIntersecting),
      { threshold: 0, rootMargin: "-60px 0px 0px 0px" },
    );
    if (heroRef.current) obs.observe(heroRef.current);
    return () => obs.disconnect();
  }, []);

  // Scroll spy — highlight tab based on visible section
  useEffect(() => {
    const observers = {};
    Object.entries(sectionRefs).forEach(([id, ref]) => {
      if (!ref.current) return;
      observers[id] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveTab(id);
        },
        { threshold: 0.3, rootMargin: "-80px 0px -40% 0px" },
      );
      observers[id].observe(ref.current);
    });
    return () => Object.values(observers).forEach((o) => o.disconnect());
  }, []);

  const handleTabClick = (id) => {
    setActiveTab(id);
    const ref = sectionRefs[id];
    if (ref.current) {
      const offset = 80; // account for sticky tabs bar
      const top =
        ref.current.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleExpandAll = useCallback(() => {
    const next = !allExpanded;
    setAllExpanded(next);
    if (next) {
      const all = {};
      course.modules.forEach((_, i) => {
        all[i] = true;
      });
      setExpandedModules(all);
    } else {
      setExpandedModules({});
    }
  }, [allExpanded, course.modules]);

  const toggleModule = useCallback((idx) => {
    setExpandedModules((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "curriculum", label: "Curriculum" },
    { id: "instructor", label: "Instructor" },
    { id: "reviews", label: "Reviews" },
  ];

  const goHome = () =>
    onNavigateHome ? onNavigateHome() : (window.location.href = "/");
  const goCourses = () =>
    onNavigateCourses
      ? onNavigateCourses()
      : (window.location.href = "/courses");
  const goCategory = () =>
    onNavigateCategory
      ? onNavigateCategory(course.category)
      : (window.location.href = `/courses?category=${encodeURIComponent(course.category)}`);

  return (
    <div className="sc-root">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="sc-hero" ref={heroRef}>
        <div className="sc-hero-bg" />
        <div className="sc-hero-grid" />
        <div className="sc-container sc-hero-inner">
          <div className="sc-hero-left">
            <div className="sc-breadcrumb">
              <button
                className="sc-breadcrumb-home"
                onClick={goHome}
                title="Home"
              >
                <HomeIcon />
              </button>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <button className="sc-breadcrumb-link" onClick={goCourses}>
                Courses
              </button>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <button className="sc-breadcrumb-link" onClick={goCategory}>
                {course.category}
              </button>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <span className="active">
                {course.title.split(" ").slice(0, 3).join(" ")}…
              </span>
            </div>

            <div className="sc-badge-row">
              <span className="sc-badge hot">🔥 Bestseller</span>
              <span className="sc-badge level">{course.level}</span>
              <span className="sc-badge lang">{course.language}</span>
            </div>

            <h1 className="sc-hero-title">
              {course.title.replace("2025", "")}
              <em>2025</em>
            </h1>
            <p className="sc-hero-subtitle">{course.subtitle}</p>

            <div className="sc-rating-row">
              <StarRating value={course.rating} />
              <span className="sc-rating-val">{course.rating}</span>
              <span className="sc-rating-count">
                ({course.ratingCount.toLocaleString()} ratings)
              </span>
              <span className="sc-enrolled">
                {course.enrolledCount.toLocaleString()} students
              </span>
            </div>

            <div className="sc-meta-pills">
              {[
                [
                  <svg
                    key="c"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>,
                  `${course.totalHours} total`,
                ],
                [
                  <svg
                    key="l"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  </svg>,
                  `${course.totalLessons} lessons`,
                ],
                [
                  <svg
                    key="s"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>,
                  "Lifetime access",
                ],
              ].map(([icon, text], i) => (
                <div key={i} className="sc-meta-pill">
                  {icon}
                  {text}
                </div>
              ))}
            </div>

            <div className="sc-instructor-row">
              <div className="sc-inst-avatar">{course.instructor.initials}</div>
              <div>
                <span className="sc-inst-label">Instructor</span>
                <span className="sc-inst-name">{course.instructor.name}</span>
              </div>
            </div>
          </div>

          <div className="sc-hero-aside">
            <EnrollCard course={course} />
          </div>
        </div>
      </section>

      {/* ── MOBILE STICKY CTA ─────────────────────────────────────────────── */}
      <div className={`sc-mobile-cta ${pastHero ? "visible" : ""}`}>
        <div className="sc-mobile-cta-inner">
          <div>
            <span className="sc-mobile-price">${course.discountPrice}</span>
            <span className="sc-mobile-orig">${course.price}</span>
          </div>
          <button className="sc-enroll-btn sc-mobile-enroll">Enroll Now</button>
        </div>
      </div>

      {/* ── PAGE BODY ─────────────────────────────────────────────────────── */}
      <div className="sc-page-body">
        <div className="sc-container sc-body-grid">
          <main className="sc-content-col">
            {/* TABS — click scrolls to section, spy highlights active */}
            <div className="sc-tabs-bar">
              <div className="sc-tabs">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    className={`sc-tab ${activeTab === tab.id ? "active" : ""}`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ALL SECTIONS ALWAYS VISIBLE */}
            <div className="sc-tab-panels">
              {/* OVERVIEW */}
              <div ref={sectionRefs.overview} className="sc-tab-content">
                <div className="sc-section-block">
                  <h2 className="sc-section-heading">What You'll Learn</h2>
                  <div className="sc-outcomes-grid">
                    {course.outcomes.map((o, i) => (
                      <div key={i} className="sc-outcome-item">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {o}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="sc-section-block">
                  <h2 className="sc-section-heading">Requirements</h2>
                  <ul className="sc-req-list">
                    {course.prerequisites.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
                <div className="sc-section-block">
                  <h2 className="sc-section-heading">About This Course</h2>
                  <div
                    className={`sc-desc-text ${descExpanded ? "expanded" : ""}`}
                  >
                    {course.description.split("\n\n").map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                  <button
                    className="sc-show-more-btn"
                    onClick={() => setDescExpanded((e) => !e)}
                  >
                    {descExpanded ? "Show less ↑" : "Show more ↓"}
                  </button>
                </div>
                <div className="sc-section-block">
                  <h2 className="sc-section-heading">Who Is This For?</h2>
                  <ul className="sc-req-list">
                    {course.targetAudience.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* CURRICULUM */}
              <div
                ref={sectionRefs.curriculum}
                className="sc-tab-content sc-section-divider"
              >
                <h2 className="sc-section-heading">Course Modules</h2>
                <div className="sc-curr-header">
                  <div className="sc-curr-stats">
                    <span>{course.modules.length} modules</span>
                    <span className="sc-sep">·</span>
                    <span>{course.totalLessons} lessons</span>
                    <span className="sc-sep">·</span>
                    <span>{course.totalHours} total</span>
                  </div>
                  <button className="sc-expand-all" onClick={handleExpandAll}>
                    {allExpanded ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="sc-accordion">
                  {course.modules.map((mod, idx) => (
                    <div
                      key={mod.id}
                      className={`sc-acc-item ${expandedModules[idx] ? "open" : ""}`}
                    >
                      <button
                        className="sc-acc-header"
                        onClick={() => toggleModule(idx)}
                      >
                        <div className="sc-acc-left">
                          <span className="sc-acc-chevron">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </span>
                          <span className="sc-acc-title">{mod.title}</span>
                        </div>
                        <div className="sc-acc-right">
                          <span className="sc-acc-count">
                            {mod.lessonCount} lessons
                          </span>
                          <span className="sc-acc-dur">{mod.duration}</span>
                        </div>
                      </button>
                      <div className="sc-acc-body">
                        {mod.lessons.map((lesson) => (
                          <div key={lesson.id} className="sc-lesson-item">
                            <div className={`sc-lesson-icon ${lesson.type}`}>
                              <LessonTypeIcon type={lesson.type} />
                            </div>
                            <span className="sc-lesson-title">
                              {lesson.title}
                            </span>
                            {lesson.isFreePreview && (
                              <span className="sc-lesson-tag preview">
                                Free Preview
                              </span>
                            )}
                            {lesson.type === "quiz" && (
                              <span className="sc-lesson-tag quiz">Quiz</span>
                            )}
                            {lesson.type === "article" && (
                              <span className="sc-lesson-tag article">
                                Article
                              </span>
                            )}
                            <span className="sc-lesson-dur">
                              {lesson.duration || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* INSTRUCTOR */}
              <div
                ref={sectionRefs.instructor}
                className="sc-tab-content sc-section-divider"
              >
                <h2 className="sc-section-heading">Instructor</h2>
                <div className="sc-instructor-card">
                  <div className="sc-inst-card-top">
                    <div className="sc-inst-card-avatar">
                      {course.instructor.initials}
                    </div>
                    <div className="sc-inst-card-info">
                      <h3 className="sc-inst-card-name">
                        {course.instructor.name}
                      </h3>
                      <p className="sc-inst-card-title">
                        {course.instructor.title}
                      </p>
                      <div className="sc-inst-stats">
                        {[
                          [
                            <svg
                              key="r"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>,
                            `${course.instructor.rating} Rating`,
                          ],
                          [
                            <svg
                              key="s"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                            </svg>,
                            `${course.instructor.students.toLocaleString()} students`,
                          ],
                          [
                            <svg
                              key="c"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                            </svg>,
                            `${course.instructor.courses} courses`,
                          ],
                        ].map(([icon, text], i) => (
                          <div key={i} className="sc-inst-stat">
                            {icon}
                            {text}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="sc-inst-bio">{course.instructor.bio}</p>
                </div>
              </div>

              {/* REVIEWS */}
              <div
                ref={sectionRefs.reviews}
                className="sc-tab-content sc-section-divider"
              >
                <h2 className="sc-section-heading">Reviews</h2>
                <div className="sc-reviews-summary">
                  <div className="sc-rating-big">{course.rating}</div>
                  <div className="sc-rating-breakdown">
                    {course.ratingBreakdown.map((rb) => (
                      <RatingBar key={rb.stars} stars={rb.stars} pct={rb.pct} />
                    ))}
                  </div>
                </div>
                <div className="sc-reviews-list">
                  {course.reviews.map((review) => (
                    <div key={review.id} className="sc-review-card">
                      <div className="sc-review-top">
                        <div className="sc-review-avatar">
                          {review.initials}
                        </div>
                        <div>
                          <div className="sc-review-name">{review.name}</div>
                          <div className="sc-review-stars">
                            {"★".repeat(review.stars)}
                            {"☆".repeat(5 - review.stars)}
                          </div>
                        </div>
                        <span className="sc-review-date">{review.date}</span>
                      </div>
                      <p className="sc-review-text">{review.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </main>

          <aside className={`sc-aside-col ${pastHero ? "visible" : ""}`}>
            <div className="sc-sticky-card">
              <EnrollCard course={course} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
